import './RecordStep.css';
import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import TemplateSelector from './TemplateSelector';

export default function RecordStep() {
  const startRecording = useVoiceDumpStore(s => s.startRecording);
  const stopRecording = useVoiceDumpStore(s => s.stopRecording);
  const analyserNode = useVoiceDumpStore(s => s.analyserNode);
  const recordingDuration = useVoiceDumpStore(s => s.recordingDuration);
  const mediaRecorder = useVoiceDumpStore(s => s.mediaRecorder);
  const error = useVoiceDumpStore(s => s.error);

  const isRecording = mediaRecorder?.state === 'recording';
  const [waveformData, setWaveformData] = useState(new Array(32).fill(4));
  const animRef = useRef(null);

  // Waveform animation loop
  useEffect(() => {
    if (!analyserNode || !isRecording) {
      setWaveformData(new Array(32).fill(4));
      return;
    }

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    const update = () => {
      analyserNode.getByteFrequencyData(dataArray);
      // Sample 32 bars from the frequency data
      const bars = [];
      const step = Math.floor(dataArray.length / 32);
      for (let i = 0; i < 32; i++) {
        const value = dataArray[i * step] || 0;
        bars.push(Math.max(4, (value / 255) * 48)); // 4px min, 48px max
      }
      setWaveformData(bars);
      animRef.current = requestAnimationFrame(update);
    };

    animRef.current = requestAnimationFrame(update);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [analyserNode, isRecording]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="vd-record">
      <p className="vd-record__prompt">
        {isRecording
          ? 'Speak freely about your tasks, projects, and ideas...'
          : 'Tap the mic to start your brain dump'}
      </p>

      {/* Template Selector — only show when not recording */}
      {!isRecording && <TemplateSelector />}

      {/* Mic button */}
      <button
        className={`btn btn-primary btn-lg btn-icon vd-mic-btn ${isRecording ? 'vd-mic-btn--recording' : ''}`}
        onClick={isRecording ? stopRecording : startRecording}
        aria-label={isRecording ? 'Stop recording' : 'Start recording'}
      >
        {isRecording ? <Square size={32} /> : <Mic size={36} />}
      </button>

      {/* Waveform */}
      {isRecording ? (
        <div className="vd-waveform" aria-label="Recording in progress...">
          {waveformData.map((height, i) => (
            <div
              key={i}
              className="vd-waveform__bar"
              style={{ height: `${height}px` }}
            />
          ))}
        </div>
      ) : (
        <div className="vd-waveform">
          <span className="vd-waveform__placeholder">Ready to record</span>
        </div>
      )}

      {/* Timer */}
      <div className="vd-timer">{formatTime(recordingDuration)}</div>

      {/* Error */}
      {error === 'MIC_PERMISSION_DENIED' && (
        <div className="vd-error">
          🎤 Microphone access needed. Please allow microphone access in your browser settings.
        </div>
      )}
      {error === 'RECORDING_TOO_SHORT' && (
        <div className="vd-error">
          Recording too short. Speak for at least a few seconds.
        </div>
      )}
      {error?.startsWith('MIC_ERROR') && (
        <div className="vd-error">
          ⚠️ Microphone error: {error.replace('MIC_ERROR: ', '')}
        </div>
      )}

      <p className="vd-record__hint">
        {isRecording ? 'Tap the button again to stop' : 'Tip: mention tasks, projects, areas, deadlines, and more'}
      </p>
    </div>
  );
}
